import { useCallback, useEffect, useRef, useState } from "react";
import { nanoid } from "nanoid";
import { axiosInstance } from "@/lib/api";

/**
 * How long to wait for the server's terminal frames after asking it to stop
 * before asking again. A stop that lands while the turn is still being set up
 * takes effect as soon as setup ends, which is normally well inside this.
 */
const STOP_RETRY_MS = 5_000;
/** How long, in total, before giving up on this socket and handing off to the resume loop. */
const STOP_WATCHDOG_MS = 20_000;

/**
 * The send this tab has in flight. Its id rides on the chat request and on the
 * Stop, so the server can stop the send even before it has started it.
 */
export type LiveSend = {
  id: string;
  /** Null until a new chat's session has been created. */
  sessionId: string | null;
  /** Set by Stop; a send that has not reached the server yet then never does. */
  stopped: boolean;
};

export function newLiveSend(sessionId: string | null): LiveSend {
  // nanoid rather than crypto.randomUUID, which is missing outside secure contexts.
  return { id: nanoid(), sessionId, stopped: false };
}

/**
 * The "Stop generating" button's behaviour, shared by every agent chat hook.
 *
 * Deliberately does NOT abort the local fetch. The server terminates the stream
 * itself (it emits `aborted`, then `complete`, then `[DONE]`), so the reply's
 * partial text, thinking and tool events are finalized through the same path a
 * normal turn takes. Tearing down the reader here instead would drop us into the
 * stream-died recovery path — which, if the server-side abort had not actually
 * taken effect, would resume FOLLOWING the still-running turn and make Stop look
 * broken. Letting the server close the stream keeps the UI honest: what is on
 * screen is what the server did.
 *
 * A Stop pressed the instant the message was sent is the hard case: the chat
 * request may not have reached the server yet, or the reply is still being set
 * up. The Stop names the send (`LiveSend.id`) and the server applies it as soon
 * as that send starts. A new chat's send that has not been posted at all is
 * simply dropped by the chat hook, which checks `stopped` before posting.
 *
 * The watchdog asks again after a few seconds (a lost request), then hands off
 * to the resume loop as a last resort.
 *
 * Keyed on the SESSION, not the socket — so a second tab, or a tab that
 * refreshed mid-turn and is following via the resume loop, can stop the turn too.
 */
export function useStopChatTurn(params: {
  /** The open session: what a tab following a reply (no send of its own) stops. */
  sessionId: string | null;
  abortUrlFor: (sessionId: string) => string;
  /** The send this tab has in flight, if any. */
  liveSendRef: { current: LiveSend | null };
  isStreaming: boolean;
  /** Called when the server never closed the stream — hand off to the resume loop. */
  onStalled: () => void;
}): { stopStreaming: () => void; isStopping: boolean } {
  const { sessionId, abortUrlFor, liveSendRef, isStreaming, onStalled } = params;
  const [stopRequested, setStopRequested] = useState(false);
  const [streamingWhenAsked, setStreamingWhenAsked] = useState(isStreaming);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  // Kept reachable from the watchdog timers without making them callback deps
  // (they change identity every render).
  const onStalledRef = useRef(onStalled);
  const abortUrlForRef = useRef(abortUrlFor);
  useEffect(() => {
    onStalledRef.current = onStalled;
    abortUrlForRef.current = abortUrlFor;
  });

  const clearWatchdog = () => {
    for (const timer of timersRef.current) clearTimeout(timer);
    timersRef.current = [];
  };

  // A stop only applies to the turn it was asked for. Deriving it this way means
  // whatever ended that turn — our stop, a normal completion, or the resume loop
  // settling — returns the button to idle with nothing to reset, and a fresh
  // turn always starts stoppable. (React's documented "adjust state when a prop
  // changes" pattern; an effect here would just cause a second render pass.)
  if (streamingWhenAsked !== isStreaming) {
    setStreamingWhenAsked(isStreaming);
    if (stopRequested) setStopRequested(false);
  }
  const isStopping = isStreaming && stopRequested;

  // The turn is over, so there is nothing left to rescue — a watchdog still
  // pending here would fire into the NEXT turn and settle it out from under
  // itself. (Clearing timers only; no state, so no cascading render.)
  useEffect(() => {
    if (!isStreaming) clearWatchdog();
  }, [isStreaming]);

  useEffect(() => clearWatchdog, []);

  const stopStreaming = useCallback(() => {
    if (!isStreaming || stopRequested) return;
    const send = liveSendRef.current;
    if (send) send.stopped = true;
    const target = send ? send.sessionId : sessionId;
    setStopRequested(true);
    // A new chat still creating its session: the send drops itself before it
    // posts anything, so there is nothing on the server to stop.
    if (!target) return;

    const body = send ? { turnId: send.id } : {};
    const requestStop = () => axiosInstance.post(abortUrlForRef.current(target), body);
    void requestStop().catch((error) => {
      console.error("Failed to stop generation:", error);
      // Let them try again rather than leaving the button stuck on "Stopping…".
      clearWatchdog();
      setStopRequested(false);
    });
    clearWatchdog();
    timersRef.current = [
      setTimeout(() => void requestStop().catch(() => {}), STOP_RETRY_MS),
      setTimeout(() => {
        timersRef.current = [];
        onStalledRef.current();
      }, STOP_WATCHDOG_MS),
    ];
  }, [isStreaming, stopRequested, liveSendRef, sessionId]);

  return { stopStreaming, isStopping };
}
